/**
 * The Unplugged Wear — Wishlist
 * Powered by localStorage. No app required.
 *
 * API (window.Wishlist):
 *   .add(productId, productData)   — add a product
 *   .remove(productId)             — remove a product
 *   .toggle(productId, productData)— add if absent, remove if present
 *   .has(productId)                — returns boolean
 *   .getAll()                      — returns array of saved product objects
 *   .count()                       — returns integer
 *
 * Events dispatched on document:
 *   wishlist:change   — fired on every add/remove, detail: { productId, action }
 */

(function () {
  'use strict';

  var STORAGE_KEY = 'tuw_wishlist';

  // ── Core storage helpers ──────────────────────────────────────────────────

  function load() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
    } catch (e) {
      return {};
    }
  }

  function save(data) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
      // localStorage full or blocked — fail silently
    }
  }

  function dispatch(productId, action) {
    document.dispatchEvent(
      new CustomEvent('wishlist:change', {
        bubbles: true,
        detail: { productId: productId, action: action }
      })
    );
  }

  // ── Public API ────────────────────────────────────────────────────────────

  var Wishlist = {
    add: function (productId, productData) {
      var data = load();
      data[String(productId)] = productData || { id: productId };
      save(data);
      dispatch(productId, 'add');
    },

    remove: function (productId) {
      var data = load();
      delete data[String(productId)];
      save(data);
      dispatch(productId, 'remove');
    },

    toggle: function (productId, productData) {
      if (this.has(productId)) {
        this.remove(productId);
      } else {
        this.add(productId, productData);
      }
    },

    has: function (productId) {
      return !!load()[String(productId)];
    },

    getAll: function () {
      var data = load();
      return Object.values(data);
    },

    count: function () {
      return Object.keys(load()).length;
    },

    clear: function () {
      save({});
      document.dispatchEvent(new CustomEvent('wishlist:change', { bubbles: true, detail: { action: 'clear' } }));
    }
  };

  window.Wishlist = Wishlist;

  // ── Wishlist button behaviour ─────────────────────────────────────────────
  // Handles any element with [data-wishlist-btn] and [data-product-id].
  // Also reads optional data attributes for product details:
  //   data-product-title, data-product-url,
  //   data-product-image, data-product-price, data-product-vendor

  function updateBtn(btn) {
    var id = btn.dataset.productId;
    var active = Wishlist.has(id);
    btn.setAttribute('aria-pressed', String(active));
    btn.classList.toggle('wishlist-btn--active', active);

    var label = btn.querySelector('.wishlist-btn__label');
    if (label) {
      label.textContent = active ? btn.dataset.labelRemove || 'Wishlisted' : btn.dataset.labelAdd || 'Wishlist';
    }

    var icon = btn.querySelector('.wishlist-btn__icon');
    if (icon) {
      icon.innerHTML = active ? heartFilledSVG() : heartOutlineSVG();
    }
  }

  function heartOutlineSVG() {
    return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>';
  }

  function heartFilledSVG() {
    return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="currentColor" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>';
  }

  function handleBtnClick(e) {
    var btn = e.target.closest('[data-wishlist-btn]');
    if (!btn) return;

    var id = btn.dataset.productId;
    if (!id) return;

    var productData = {
      id:     id,
      title:  btn.dataset.productTitle  || '',
      url:    btn.dataset.productUrl    || '',
      image:  btn.dataset.productImage  || '',
      price:  btn.dataset.productPrice  || '',
      vendor: btn.dataset.productVendor || ''
    };

    Wishlist.toggle(id, productData);
  }

  // ── Wishlist count badge ──────────────────────────────────────────────────

  function updateCountBadges() {
    var count = Wishlist.count();
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = count;
      el.style.display = count === 0 ? 'none' : '';
    });
  }

  // ── Wishlist page renderer ────────────────────────────────────────────────

  function renderWishlistPage() {
    var container = document.getElementById('wishlist-page-container');
    if (!container) return;

    var items = Wishlist.getAll();
    var emptyState = document.getElementById('wishlist-empty');
    var grid = document.getElementById('wishlist-grid');

    if (items.length === 0) {
      if (emptyState) emptyState.style.display = '';
      if (grid) grid.innerHTML = '';
      return;
    }

    if (emptyState) emptyState.style.display = 'none';

    if (grid) {
      grid.innerHTML = items.map(function (p) {
        return [
          '<div class="wishlist-card" data-product-id="' + p.id + '">',
          '  <a href="' + (p.url || '#') + '" class="wishlist-card__image-link">',
          p.image
            ? '    <img src="' + p.image + '" alt="' + escapeHtml(p.title) + '" class="wishlist-card__image" loading="lazy" width="400" height="400">'
            : '    <div class="wishlist-card__image wishlist-card__image--placeholder"></div>',
          '  </a>',
          '  <div class="wishlist-card__info">',
          // '    <p class="wishlist-card__vendor">' + escapeHtml(p.vendor || '') + '</p>',
          '    <a href="' + (p.url || '#') + '" class="wishlist-card__title">' + escapeHtml(p.title) + '</a>',
          '    <p class="wishlist-card__price">' + escapeHtml(p.price || '') + '</p>',
          '  </div>',
          '  <div class="wishlist-card__actions">',
          '    <a href="' + (p.url || '#') + '" class="wishlist-card__view-btn">View Product</a>',
          '    <button',
          '      class="wishlist-card__remove-btn"',
          '      data-wishlist-btn',
          '      data-product-id="' + p.id + '"',
          '      aria-label="Remove from wishlist"',
          '    >',
          '      <span class="wishlist-btn__icon">' + heartFilledSVG() + '</span>',
          '      <span>Remove</span>',
          '    </button>',
          '  </div>',
          '</div>'
        ].join('\n');
      }).join('\n');
    }
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // ── Init ─────────────────────────────────────────────────────────────────

  document.addEventListener('click', handleBtnClick);

  document.addEventListener('wishlist:change', function () {
    // Re-sync all buttons on the page
    document.querySelectorAll('[data-wishlist-btn]').forEach(updateBtn);
    updateCountBadges();
    renderWishlistPage();
  });

  // On page load: sync all buttons + badges + wishlist page
  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-wishlist-btn]').forEach(updateBtn);
    updateCountBadges();
    renderWishlistPage();
  });

})();
